/* assets/cart-drawer-custom.js — v4 « panier rapide »
   - Le panier s'ouvre dès le clic, avec une ligne provisoire « Ajout au panier… »
   - Le contenu final du panier arrive dans la même réponse que l'ajout
     (paramètre `sections` de l'API panier) : une requête de moins qu'avant
   - Quantités / suppression : effet affiché tout de suite, une seule requête,
     les clics rapprochés sur + / − sont regroupés
   - Le panneau n'est plus recréé à chaque mise à jour (seul son contenu change)
   - Inchangé : codes promo, upsell, icône panier, synchro du compteur,
     protection contre le double ajout et les formulaires de bundles
*/

(() => {
  const SECTION = 'cart-drawer-custom';
  const drawerSel = '[data-cdrawer]';
  const getDrawer = () => document.querySelector(drawerSel);
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  const sectionsUrl = () => window.location.pathname;
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const LANG = (document.documentElement.lang || 'fr').slice(0, 2).toLowerCase();
  const T = {
    fr: { adding: 'Ajout au panier…', error: 'Une erreur est survenue. Veuillez réessayer.' },
    en: { adding: 'Adding to cart…', error: 'Something went wrong. Please try again.' },
    es: { adding: 'Añadiendo al carrito…', error: 'Se ha producido un error. Inténtalo de nuevo.' },
    it: { adding: 'Aggiunta al carrello…', error: 'Si è verificato un errore. Riprova.' }
  }[LANG] || { adding: 'Ajout au panier…', error: 'Une erreur est survenue. Veuillez réessayer.' };

  /* =========================
     BODY LOCK
  ========================= */
  const lockBody = () => {
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollBarWidth > 0) document.body.style.paddingRight = `${scrollBarWidth}px`;
  };

  const unlockBody = () => {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  };

  const open = () => {
    const d = getDrawer();
    if (!d) return;
    if (!d.hidden) return;
    d.hidden = false;
    d.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('cdrawer-open');
    lockBody();
  };

  const close = () => {
    const d = getDrawer();
    if (!d) return;
    d.setAttribute('aria-hidden', 'true');
    d.hidden = true;
    document.documentElement.classList.remove('cdrawer-open');
    unlockBody();
  };

  /* =========================
     LOADING + ERRORS
  ========================= */
  let loadingCount = 0;
  const setLoading = (on) => {
    loadingCount = Math.max(0, loadingCount + (on ? 1 : -1));
    const d = getDrawer();
    if (d) d.classList.toggle('is-loading', loadingCount > 0);
  };

  const showError = (msg) => {
    const content = getDrawer()?.querySelector('.cdrawer__content');
    if (!content) return;
    let el = content.querySelector('.cdrawer__error');
    if (!el) {
      el = document.createElement('div');
      el.className = 'cdrawer__error';
      el.setAttribute('role', 'alert');
      content.prepend(el);
    }
    el.textContent = msg || T.error;
  };

  /* =========================
     CART JSON + HEADER COUNT
  ========================= */
  const fetchCart = async () => {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return await res.json();
  };

  const setHeaderCount = (count) => {
    const txt = count >= 100 ? '99+' : String(count);
    document
      .querySelectorAll('cart-count, [data-cart-count]:not(.cdrawer__body), .cart-count, .header__cart-count, .header-cart-count, [aria-label*="cart count"]')
      .forEach((el) => {
        el.textContent = txt;
        if (count === 0) el.setAttribute('hidden', '');
        else el.removeAttribute('hidden');
      });
  };

  // Compteur mis à jour tout de suite depuis le panier reçu, puis confirmé en arrière-plan
  // (l'événement cart:updated est conservé pour les autres scripts).
  const syncHeaderCartCount = () => {
    const body = getDrawer()?.querySelector('.cdrawer__body[data-cart-count]');
    if (body) setHeaderCount(Number(body.dataset.cartCount || 0));
    fetchCart()
      .then((cart) => {
        setHeaderCount(Number(cart.item_count || 0));
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        ensureGift(cart);
      })
      .catch(() => {});
  };

  /* =========================
     HALLOWEEN — poudre coiffante offerte dès 3 pots de camouflage, le 20 octobre 2026 uniquement (heure de Paris).
     La gratuité vient d'une remise Shopify (« Achetez X, obtenez Y ») ou de Kaching : le thème ne fait qu'ajouter
     la poudre au panier, puis la retire aussitôt si elle n'est pas à 0 € (remise absente, terminée ou non cumulable).
     Une poudre déjà présente dans le panier n'est pas doublée ; une poudre retirée par le client n'est pas remise.
  ========================= */
  const HW_GIFT = {
    until: Date.UTC(2026, 9, 20, 21, 59, 59), // 20/10/2026 23:59:59 heure de Paris
    pots: [10094875705690, 9953683833178], // Camouflage capillaire homme + femme
    handle: 'poudre-coiffante-texturisante',
    min: 3
  };
  let giftBusy = false;
  let giftVariant = null;
  const giftOff = () => { try { return sessionStorage.getItem('hw_gift_off') === '1'; } catch (e) { return false; } };
  const setGiftOff = () => { try { sessionStorage.setItem('hw_gift_off', '1'); } catch (e) {} };
  const isGiftLine = (i) => !!(i.properties && i.properties._hw_gift);
  const removeKey = (key) =>
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    });

  async function ensureGift(cart) {
    if (giftBusy || !cart || !Array.isArray(cart.items)) return;
    const live = Date.now() < HW_GIFT.until;
    const gifts = cart.items.filter(isGiftLine);
    if (!live && !gifts.length) return;
    const pots = cart.items
      .filter((i) => HW_GIFT.pots.includes(i.product_id) && i.final_line_price > 0)
      .reduce((n, i) => n + i.quantity, 0);
    const eligible = live && pots >= HW_GIFT.min;

    giftBusy = true;
    let changed = false;
    try {
      // Retrait : plus éligible, ou poudre facturée
      for (const g of gifts) {
        if (!eligible || g.final_line_price > 0) {
          await removeKey(g.key);
          changed = true;
          if (eligible) setGiftOff(); // la remise ne s'applique pas : on n'insiste pas pendant la session
        }
      }
      // Ajout (sauf si une poudre est déjà dans le panier : la remise s'appliquera sur celle-ci)
      const hasPowder = cart.items.some((i) => i.handle === HW_GIFT.handle);
      if (eligible && !gifts.length && !hasPowder && !giftOff()) {
        if (!giftVariant) {
          const p = await (await fetch(`/products/${HW_GIFT.handle}.js`)).json();
          giftVariant = p && p.variants && p.variants[0] && p.variants[0].id;
        }
        if (giftVariant) {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: giftVariant, quantity: 1, properties: { _hw_gift: '1' } }] })
          });
          changed = true;
          // Vérification : la poudre doit être à 0 €, sinon on la retire
          const after = await fetchCart();
          const line = after.items.find(isGiftLine);
          if (line && line.final_line_price > 0) {
            await removeKey(line.key);
            setGiftOff();
          }
        }
      }
      if (changed) {
        await refreshDrawer();
        const c = await fetchCart();
        setHeaderCount(Number(c.item_count || 0));
      }
    } catch (err) {
      console.error(err);
    } finally {
      giftBusy = false;
    }
  }

  // Le 20 : vérification au chargement (panier déjà rempli lors d'une visite précédente)
  if (Date.now() < HW_GIFT.until) {
    fetchCart().then(ensureGift).catch(() => {});
  }

  /* =========================
     SECTION SWAP (keep panel)
  ========================= */
  // Remplace l'en-tête et le contenu, pas le panneau : pas de clignotement ni de nouvelle animation.
  const swapFromHTML = (html) => {
    const next = parse(html).querySelector(drawerSel);
    if (!next) return false;
    const cur = getDrawer();
    if (!cur) {
      document.body.appendChild(next);
      return true;
    }
    const nb = next.querySelector('.cdrawer__body');
    const cb = cur.querySelector('.cdrawer__body');
    if (!nb || !cb) {
      const wasOpen = !cur.hidden;
      cur.replaceWith(next);
      if (wasOpen) {
        next.hidden = false;
        next.setAttribute('aria-hidden', 'false');
      }
      return true;
    }
    const scroll = cb.querySelector('.cdrawer__content')?.scrollTop || 0;
    const nh = next.querySelector('.cdrawer__header');
    const ch = cur.querySelector('.cdrawer__header');
    if (nh && ch) ch.replaceWith(nh);
    cb.replaceWith(nb);
    const nc = nb.querySelector('.cdrawer__content');
    if (nc && scroll) nc.scrollTop = scroll;
    return true;
  };

  const refreshDrawer = async () => {
    const url = `${sectionsUrl()}?section_id=${SECTION}&_=${Date.now()}`;
    const res = await fetch(url, { headers: { Accept: 'text/html' } });
    swapFromHTML(await res.text());
  };

  const applySections = async (data) => {
    const html = data && data.sections && data.sections[SECTION];
    if (html) swapFromHTML(html);
    else await refreshDrawer();
  };

  // Les réponses arrivées en retard (clics rapprochés) ne doivent pas écraser une version plus récente.
  let reqSeq = 0;

  const send = async (url, { json, form } = {}) => {
    const seq = ++reqSeq;
    let body;
    const headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
    if (form) {
      body = form;
      body.append('sections', SECTION);
      body.append('sections_url', sectionsUrl());
    } else {
      body = JSON.stringify({ ...(json || {}), sections: SECTION, sections_url: sectionsUrl() });
      headers['Content-Type'] = 'application/json';
    }
    const res = await fetch(url, { method: 'POST', headers, body });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      await refreshDrawer().catch(() => {});
      showError(data.description || data.message);
      return data;
    }
    if (seq === reqSeq) await applySections(data);
    return data;
  };

  /* =========================
     LIGNE PROVISOIRE (ajout)
  ========================= */
  const pendingLine = (form) => {
    const content = getDrawer()?.querySelector('.cdrawer__content');
    if (!content) return null;
    let list = content.querySelector('.cdrawer__items');
    if (!list) {
      list = document.createElement('ul');
      list.className = 'cdrawer__items';
      list.setAttribute('role', 'list');
      content.prepend(list);
    }
    let img = (document.querySelector('meta[property="og:image"]')?.content || '').replace(/^http:/, 'https:');
    if (img) img += (img.includes('?') ? '&' : '?') + 'width=160';
    const title = (document.querySelector('meta[property="og:title"]')?.content || '').trim();
    const bar = form?.querySelector('.kaching-bundles__bar--selected');
    const pack = bar?.querySelector('.kaching-bundles__bar-title')?.textContent.replace(/\s+/g, ' ').trim() || '';
    const price = bar?.querySelector('.kaching-bundles__bar-price')?.textContent.replace(/\s+/g, ' ').trim() || '';

    const li = document.createElement('li');
    li.className = 'citem citem--pending';
    li.innerHTML =
      `<span class="citem__img">${img ? `<img src="${esc(img)}" alt="" width="72" height="72">` : ''}</span>` +
      `<div class="citem__main">` +
      (title ? `<div class="citem__name">${esc(title)}</div>` : '') +
      (pack || price ? `<div class="citem__variant">${esc([pack, price].filter(Boolean).join(' · '))}</div>` : '') +
      `<div class="citem__pending-note" role="status"><span class="cspin" aria-hidden="true"></span>${esc(T.adding)}</div>` +
      `</div>`;
    list.prepend(li);
    content.scrollTop = 0;
    return li;
  };

  /* =========================
     COUPONS (stay in drawer)
  ========================= */
  const cartUpdateUrl = window.FoxTheme?.routes?.cart_update_url || '/cart/update.js';

  const setCouponError = (msg) => {
    const d = getDrawer();
    const el = d?.querySelector('.ccoupon__error');
    if (!el) return;

    if (!msg) {
      el.hidden = true;
      el.textContent = '';
    } else {
      el.hidden = false;
      el.textContent = msg;
      d.querySelector('.ccoupon-wrap')?.setAttribute('open', '');
    }
  };

  // persist codes for Liquid/UI via cart attribute
  const applyDiscountCodes = async (codesCsv) => {
    const fd = new FormData();
    fd.append('discount', codesCsv);
    fd.append('attributes[cd_discount_codes]', codesCsv || '');

    setLoading(true);
    try {
      const data = await send(cartUpdateUrl, { form: fd });
      open();
      syncHeaderCartCount();
      return data;
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     CART MUTATIONS (add/change)
  ========================= */
  const addItems = async (items) => {
    setLoading(true);
    try {
      await send('/cart/add.js', { json: { items } });
      open();
      syncHeaderCartCount();
    } finally {
      setLoading(false);
    }
  };

  // Ouverture dès le clic sur « Ajouter au panier » : Kaching ajoute d'abord le cadeau puis renvoie
  // le formulaire (≈ 1 s plus tard) ; on n'attend pas ce renvoi pour montrer le panier.
  let early = null;
  const earlyOpen = (form) => {
    if (early || addInFlight) return;
    open();
    setLoading(true);
    const pending = pendingLine(form);
    const timer = setTimeout(async () => {
      // Filet de sécurité : le formulaire n'a jamais été envoyé → on affiche le vrai panier
      if (!early || early.timer !== timer) return;
      early = null;
      pending?.remove();
      setLoading(false);
      await refreshDrawer().catch(() => {});
    }, 10000);
    early = { form, pending, timer };
  };

  const addFromForm = async (form) => {
    // Retour immédiat : panier ouvert + ligne provisoire pendant que Shopify (et Kaching) ajoutent le produit
    let pending;
    if (early && early.form === form) {
      clearTimeout(early.timer);
      pending = early.pending;
      early = null;
    } else {
      open();
      setLoading(true);
      pending = pendingLine(form);
    }
    try {
      // FormData complet : id, quantity, properties[...] (Kaching), selling_plan…
      await send('/cart/add.js', { form: new FormData(form) });
    } catch (err) {
      console.error(err);
      pending?.remove();
      await refreshDrawer().catch(() => {});
      showError();
    } finally {
      setLoading(false);
      syncHeaderCartCount();
    }
  };

  const changeLine = async (line, quantity) => {
    setLoading(true);
    try {
      await send('/cart/change.js', { json: { line, quantity } });
      syncHeaderCartCount();
    } catch (err) {
      console.error(err);
      await refreshDrawer().catch(() => {});
      showError();
    } finally {
      setLoading(false);
    }
  };

  // + / − : la quantité affichée change tout de suite, la requête part 300 ms après le dernier clic
  const qtyTimers = new Map();
  const scheduleQty = (item, line, quantity) => {
    const input = item.querySelector('[data-cdrawer-qty]');
    if (input) input.value = String(quantity);
    item.classList.add('is-updating');
    clearTimeout(qtyTimers.get(line));
    qtyTimers.set(
      line,
      setTimeout(() => {
        qtyTimers.delete(line);
        changeLine(line, quantity);
      }, 300)
    );
  };

  /* =========================================================
     SAFE ADD-TO-CART INTERCEPT (avoid bundles + avoid double add)
  ========================================================= */
  let addInFlight = false;

  const isBundleLikeForm = (form) => {
    // bundles often submit items[0][id] ... items[n][id]
    return !!form.querySelector('[name^="items["], [name^="items[" i]');
  };

  const shouldInterceptAddToCartForm = (form) => {
    if (isBundleLikeForm(form)) return false;

    const hasVariantId = !!form.querySelector('input[name="id"]');
    if (!hasVariantId) return false;

    const action = (form.getAttribute('action') || '').toLowerCase();

    if (action.includes('/cart/add')) return true;

    const looksLikeProductForm =
      form.matches('form[action=""], form[action="/"], form.product-form') ||
      !!form.querySelector('[type="submit"][name="add"], [data-add-to-cart], [name="add"]');

    if (looksLikeProductForm) return true;

    if (form.hasAttribute('data-cdrawer-add-to-cart')) return true;

    return false;
  };

  // Clic sur le bouton d'ajout (ou sur la barre collante, qui clique ce bouton) : panier ouvert tout de suite
  window.addEventListener(
    'click',
    (e) => {
      const btn = e.target.closest?.('button[type="submit"], input[type="submit"], .product-form__submit');
      if (!btn || btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
      const form = btn.form || btn.closest('form');
      if (!form || !shouldInterceptAddToCartForm(form)) return;
      earlyOpen(form);
    },
    true
  );

  // Capture phase to run BEFORE theme handlers (prevents double-add)
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;

      if (!shouldInterceptAddToCartForm(form)) return;

      e.preventDefault();
      e.stopPropagation();

      if (addInFlight) return;
      addInFlight = true;

      addFromForm(form)
        .catch(console.error)
        .finally(() => {
          addInFlight = false;
        });
    },
    true
  );

  /* =========================
     CLICK HANDLERS (drawer UI)
  ========================= */
  document.addEventListener('click', async (e) => {
    const t = e.target;

    if (t.closest('[data-cdrawer-close]') || t.closest('[data-cdrawer-overlay]')) {
      e.preventDefault();
      close();
      return;
    }

    const checkout = t.closest('[data-cdrawer-checkout]');
    if (checkout) {
      e.preventDefault();
      checkout.classList.add('is-going');
      checkout.setAttribute('aria-busy', 'true');
      window.location.href = '/checkout';
      return;
    }

    // apply coupon
    const couponBtn = t.closest('.ccoupon__go');
    if (couponBtn) {
      e.preventDefault();
      e.stopPropagation();

      const drawer = getDrawer();
      const input = drawer?.querySelector('.ccoupon__input');
      const codesWrap = drawer?.querySelector('.cdiscounts');

      const newCode = (input?.value || '').trim();
      if (!newCode) {
        input?.focus();
        return;
      }

      const existingCsv = (codesWrap?.getAttribute('data-applied-codes') || '').trim();
      const existing = existingCsv ? existingCsv.split(',').map((s) => s.trim()).filter(Boolean) : [];

      const merged = Array.from(new Set([...existing, newCode]));
      setCouponError('');

      const resp = await applyDiscountCodes(merged.join(','));
      open();

      const bad = resp?.discount_codes?.find(
        (dc) => (dc.code || '').toLowerCase() === newCode.toLowerCase() && dc.applicable === false
      );
      if (bad) setCouponError('Code invalide ou non applicable.');

      return;
    }

    // remove coupon chip with CLEAR mode
    const removeBtn = t.closest('[data-cdrawer-discount-remove]');
    if (removeBtn) {
      e.preventDefault();
      e.stopPropagation();

      const codeToRemove = (removeBtn.dataset.code || '').trim();

      // If we don't know the code (allocations only), clear discounts
      if (codeToRemove === '__clear__') {
        await applyDiscountCodes('');
        open();
        return;
      }

      const drawer = getDrawer();
      const codesWrap = drawer?.querySelector('.cdiscounts');

      const existingCsv = (codesWrap?.getAttribute('data-applied-codes') || '').trim();
      const existing = existingCsv ? existingCsv.split(',').map((s) => s.trim()).filter(Boolean) : [];

      const remaining = existing.filter((c) => c.toLowerCase() !== codeToRemove.toLowerCase());
      setCouponError('');

      await applyDiscountCodes(remaining.join(','));
      open();
      return;
    }

    // upsell add (still ok as pure id)
    const upsell = t.closest('[data-cdrawer-upsell-add]');
    if (upsell) {
      e.preventDefault();
      e.stopPropagation();

      if (addInFlight) return;
      addInFlight = true;
      upsell.classList.add('is-adding');
      upsell.setAttribute('aria-busy', 'true');

      const id = Number(upsell.dataset.variantId);
      addItems([{ id, quantity: 1 }])
        .catch(console.error)
        .finally(() => {
          addInFlight = false;
        });
      return;
    }

    // Upsell carousel nav
    const prev = t.closest('[data-upsell-prev]');
    const next = t.closest('[data-upsell-next]');
    if (prev || next) {
      e.preventDefault();

      const drawer = getDrawer();
      const rail = drawer?.querySelector('[data-upsell-rail]');
      if (!rail) return;

      const amount = Math.round(rail.clientWidth * 0.85);
      rail.scrollBy({
        left: next ? amount : -amount,
        behavior: 'smooth'
      });

      return;
    }

    // qty/remove per line
    const item = t.closest('.citem[data-line]');
    if (!item) return;

    const line = Number(item.dataset.line);
    const qtyInput = item.querySelector('[data-cdrawer-qty]');
    const currentQty = Number(qtyInput?.value || 0);

    if (t.closest('[data-cdrawer-remove]')) {
      e.preventDefault();
      e.stopPropagation();
      if (item.hasAttribute('data-hw-gift')) setGiftOff(); // poudre offerte retirée par le client : on ne la remet pas
      clearTimeout(qtyTimers.get(line));
      qtyTimers.delete(line);
      item.classList.add('is-removing');
      changeLine(line, 0);
      return;
    }

    if (t.closest('[data-cdrawer-minus]')) {
      e.preventDefault();
      e.stopPropagation();
      const q = Math.max(0, currentQty - 1);
      if (q === 0) {
        clearTimeout(qtyTimers.get(line));
        qtyTimers.delete(line);
        item.classList.add('is-removing');
        changeLine(line, 0);
      } else {
        scheduleQty(item, line, q);
      }
      return;
    }

    if (t.closest('[data-cdrawer-plus]')) {
      e.preventDefault();
      e.stopPropagation();
      scheduleQty(item, line, currentQty + 1);
      return;
    }
  });

  // Saisie directe de la quantité au clavier
  document.addEventListener('change', (e) => {
    const input = e.target.closest?.('[data-cdrawer-qty]');
    if (!input) return;
    const item = input.closest('.citem[data-line]');
    if (!item) return;
    const q = Math.max(0, parseInt(input.value, 10) || 0);
    item.classList.add(q === 0 ? 'is-removing' : 'is-updating');
    changeLine(Number(item.dataset.line), q);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });

  /* =========================
     HEADER CART ICON => open custom drawer (avoid /cart page)
     Ouverture immédiate avec le contenu déjà chargé, actualisé en arrière-plan.
  ========================= */
  document.addEventListener(
    'click',
    async (e) => {
      const el = e.target;
      if (el.closest(drawerSel)) return;

      const a = el.closest('a[href]');
      const href = (a?.getAttribute('href') || '').trim();

      const isCartLink =
        href === '/cart' ||
        href.startsWith('/cart?') ||
        href.endsWith('/cart') ||
        href.includes('/cart?');

      const toggle = el.closest('[aria-controls="CartDrawer"], .header__icon--cart, [data-cart-toggle]');

      if (!isCartLink && !toggle) return;
      if (href && href.includes('checkout')) return;

      e.preventDefault();
      e.stopPropagation();

      open();
      setLoading(true);
      try {
        await refreshDrawer();
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    true
  );

  // Retour arrière (cache navigateur) : le bouton de paiement ne doit pas rester en attente
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    document.querySelectorAll('[data-cdrawer-checkout].is-going').forEach((b) => {
      b.classList.remove('is-going');
      b.removeAttribute('aria-busy');
    });
  });
})();
